import { useCallback, useRef, useState } from 'react';

import { normalizeMimeType, resolveFileMimeType } from '../../utils/resolve-file-mime-type';

const parseAcceptTypes = (accept: string): string[] => {
  return accept
    .split(',')
    .map((type) => type.trim())
    .filter(Boolean);
};

export const isFileAccepted = (file: File, accept: string): boolean => {
  const acceptedTypes = parseAcceptTypes(accept);

  if (acceptedTypes.length === 0) {
    return true;
  }

  const fileType = resolveFileMimeType(file);

  if (!fileType) {
    return false;
  }

  return acceptedTypes.some((type) => {
    if (type.endsWith('/*')) {
      const prefix = type.slice(0, -1);
      return fileType.startsWith(prefix);
    }

    return fileType === normalizeMimeType(type);
  });
};

interface UseAttachmentsParams {
  accept: string;
  maxFiles: number;
}

const useAttachments = ({ accept, maxFiles }: UseAttachmentsParams) => {
  const [files, setFiles] = useState<File[]>([]);
  // Keep a mirror for overflow math: setState updaters are not guaranteed to run
  // synchronously when addFiles is called after an await (e.g. image resize).
  const filesRef = useRef(files);
  filesRef.current = files;

  const addFiles = useCallback(
    (incoming: File | File[] | null) => {
      if (!incoming) {
        return { accepted: [] as File[], rejected: [] as File[], overflowCount: 0 };
      }

      const candidates = Array.isArray(incoming) ? incoming : [incoming];
      const accepted: File[] = [];
      const rejected: File[] = [];

      for (const file of candidates) {
        if (!isFileAccepted(file, accept)) {
          rejected.push(file);
          continue;
        }

        accepted.push(file);
      }

      let overflowCount = 0;

      if (accepted.length > 0) {
        const previous = filesRef.current;
        const remainingSlots = Math.max(0, maxFiles - previous.length);
        overflowCount = Math.max(0, accepted.length - remainingSlots);
        const next = [...previous, ...accepted.slice(0, remainingSlots)];
        filesRef.current = next;
        setFiles(next);
      }

      return { accepted, rejected, overflowCount };
    },
    [accept, maxFiles],
  );

  const removeFile = useCallback((index: number) => {
    const next = filesRef.current.filter((_, fileIndex) => fileIndex !== index);
    filesRef.current = next;
    setFiles(next);
  }, []);

  const clearFiles = useCallback(() => {
    filesRef.current = [];
    setFiles([]);
  }, []);

  return {
    files,
    addFiles,
    removeFile,
    clearFiles,
  };
};

export default useAttachments;
